import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { notifications } from "@mantine/notifications";
import { DateValue, TimePicker } from "@mantine/dates";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  useGetCollectionSlots,
  useGetCollectionSuggestion,
  useGetContainerDrivers,
  postScheduleCollection,
} from "@/services/LogisticsCoordinator";
import { FullContainerOverview } from "@/types";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import { isSlotSelectable } from "@/utils/CollectionSlots";
import Text from "@/components/atoms/Text";
import DatePickerInput from "@/components/atoms/DatePickerInput";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { Avatar, SectionLoader, SectionTitle, initialsOf } from "../shared";
import CollectionSlotSection from "../CollectionSlotSection";
import { earliestReported } from "./selection";
import styles from "./styles.module.css";
import shared from "../styles.module.css";

// After scheduling succeeds, keep the confirmation on screen briefly before the
// full-containers list refetches (which drops the scheduled containers and closes the panel).
const REFRESH_DELAY_MS = 3000;

const DEFAULT_TIME = "08:00";
/** Mantine's TimePicker emits partial values while typing — only query on a complete HH:mm. */
const COMPLETE_TIME = /^\d{2}:\d{2}$/;

interface CollectionDetailProps {
  /** What this pickup covers: the one selected container, or every ticked one in multiselect. Not empty. */
  containers: FullContainerOverview[];
  /** Several containers of one site booked as a single pickup. */
  multiMode?: boolean;
  /** Multiselect only: untick a container from the pickup. */
  onRemove?: (containerId: number) => void;
  /** Called once the confirmation has been on screen long enough — multiselect leaves its mode here. */
  onScheduled?: () => void;
}

/**
 * Collections detail panel. Pick a date and an arrival time, then a driver — selecting a driver
 * creates the scheduled collection in a single call (slot or time + driver). Any later change is
 * done from the Scheduled view. Mounted with a key built from the container ids by the parent, so
 * it resets whenever the selection changes.
 *
 * Two modes, decided by `slotBookingRequired` on the suggestion: the site either publishes reception
 * slots that the pickup has to fit into, or it has no slot configuration and the arrival time is
 * supplied by hand.
 *
 * In multiselect the containers share a site, so they are booked as one pickup — one date, window and
 * driver. A slot's capacity counts truck arrivals, not containers, so the group needs no more room than
 * a single container does.
 */
export default function CollectionDetail({
  containers,
  multiMode = false,
  onRemove,
  onScheduled,
}: CollectionDetailProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const container = earliestReported(containers);
  const containerIds = containers.map((c) => c.containerId);
  // Left empty until the suggestion lands, which then seeds it. Defaulting to today instead would
  // let the slots query fire for today and again for the suggested day — two fetches, one wasted.
  const [chosenDate, setChosenDate] = useState<DateValue | undefined>(undefined);
  const [chosenTime, setChosenTime] = useState(DEFAULT_TIME);
  const [selectedSlotId, setSelectedSlotId] = useState<number | null>(null);
  const [scheduledDriver, setScheduledDriver] = useState<string | null>(null);
  const refreshTimer = useRef<number | undefined>(undefined);

  // A refresh still pending when the panel goes away (multiselect switched off, another container
  // picked) is run at once rather than dropped — otherwise scheduled containers would linger in the queue.
  useEffect(
    () => () => {
      if (refreshTimer.current === undefined) return;
      window.clearTimeout(refreshTimer.current);
      queryClient.invalidateQueries({ queryKey: ["getFullContainers"] });
    },
    [queryClient]
  );

  const dayISO = chosenDate ? dayjs(chosenDate).format("YYYY-MM-DD") : "";
  const scheduled = scheduledDriver !== null;

  const { data: suggestion, isPending: suggestionPending } = useGetCollectionSuggestion(container.containerId);
  const slotMode = suggestion?.slotBookingRequired ?? false;
  // Nothing to choose from yet: the suggestion is still on its way, or it has just landed and the effect
  // below hasn't seeded the date from it (a single render, but long enough to flash a wrong empty state).
  const awaitingSuggestion = suggestionPending || (!!suggestion && chosenDate === undefined);

  // Preselect the backend's suggested date, time and slot once the suggestion lands. Falls back to
  // today when the backend has no suggestion to offer.
  useEffect(() => {
    if (!suggestion) return;
    setChosenDate(suggestion.suggestedDateTime ? new Date(suggestion.suggestedDateTime) : new Date());
    if (suggestion.suggestedDateTime) {
      setChosenTime(dayjs(suggestion.suggestedDateTime).format("HH:mm"));
    }
    setSelectedSlotId(suggestion.suggestedReceptionSlotId ?? null);
  }, [suggestion]);

  // Slot mode: the slots for the picked day carry their own free drivers, so there is no driver call.
  // "Loading" means no data for this day yet — refetching cached data stays quiet, so ticking another
  // container in multiselect (which remounts the panel) doesn't flash a loader over what is already known.
  const { data: slotData, isLoading: slotsLoading } = useGetCollectionSlots(container.containerId, dayISO, dayISO, {
    enabled: slotMode && !!dayISO && !scheduled,
  });
  const slots = slotData?.slots ?? [];
  const selectedSlot = slots.find((entry) => entry.slot.id === selectedSlotId) ?? null;

  // Non-slot mode: we supply the window ourselves, so drivers are refetched on every time change.
  const plannedDateISO =
    !slotMode && dayISO && COMPLETE_TIME.test(chosenTime) ? dayjs(`${dayISO}T${chosenTime}`).toISOString() : "";
  // Wait for the suggestion before asking: until it lands `slotMode` reads false, and this would
  // fire a driver lookup at a slot-booking site — the wrong question, against the wrong endpoint.
  const { data: driverData, isLoading: driverLookupLoading } = useGetContainerDrivers(
    container.containerId,
    plannedDateISO,
    { enabled: !suggestionPending && !slotMode && !!plannedDateISO && !scheduled }
  );

  const drivers = slotMode ? (selectedSlot?.availableDrivers ?? []) : (driverData?.drivers ?? []);
  const fleetSize = (slotMode ? slotData?.fleetSize : driverData?.fleetSize) ?? 0;
  // Slot mode reads the drivers off the slots, so they arrive with them; otherwise they are a lookup of
  // their own, re-run on every time change. Until it lands the list is unknown, not empty.
  const driversLoading = awaitingSuggestion || (slotMode ? slotsLoading : driverLookupLoading);
  // The suggestion can preselect a slot that has since passed or filled up — re-check, don't trust it.
  const timeChosen = slotMode ? !!selectedSlot && isSlotSelectable(selectedSlot) : !!plannedDateISO;

  const handleDateChange = (value: DateValue | undefined) => {
    setChosenDate(value);
    // A slot id belongs to one day — drop it rather than booking yesterday's slot.
    setSelectedSlotId(null);
  };

  // Picking a driver creates the scheduled collection(s) (slot/time + driver in one call).
  const scheduleCollection = useMutation({
    mutationFn: (driverUserId: number) =>
      postScheduleCollection({
        containerIds,
        driverUserId,
        ...(slotMode && selectedSlotId !== null
          ? { receptionSlotId: selectedSlotId }
          : { plannedDate: plannedDateISO }),
      }),
    onSuccess: (_res, driverUserId) => {
      const name = drivers.find((d) => d.userId === driverUserId)?.name ?? "";
      setScheduledDriver(name);
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.success"),
          multiMode
            ? t("containers:toast.multiAssigned", { count: containers.length, name })
            : t("containers:toast.driverAssigned", { name })
        )
      );
      // The booking consumed slot capacity and added collections to the schedule.
      queryClient.invalidateQueries({ queryKey: ["collectionSlots"] });
      queryClient.invalidateQueries({ queryKey: ["getCollections"] });
      // Delay the list refresh so the confirmation stays visible for a moment. The caller is told only
      // once the queue no longer holds these containers, so it never falls back to a stale one.
      refreshTimer.current = window.setTimeout(async () => {
        refreshTimer.current = undefined;
        await queryClient.invalidateQueries({ queryKey: ["getFullContainers"] });
        onScheduled?.();
      }, REFRESH_DELAY_MS);
    },
    onError: () => {
      notifications.show(ErrorNotificationObj(t("notifications:generic.error"), t("containers:toast.assignError")));
    },
  });

  // Booking is one call that can take a moment: a collection per container, then the notifications.
  const driverAreaBusy = driversLoading || scheduleCollection.isPending;

  return (
    <aside className={shared.detailPanel}>
      <div className={shared.detailHeader}>
        {/* TODO: waste-type tile (abbreviation) — no abbr field from the API yet. */}
        <div className={shared.detailHeaderText}>
          {multiMode ? (
            <>
              <Text size="sm">{t("containers:multiselect.panelTitle", { count: containers.length })}</Text>
              <Text display="technical" size="xs" tt="uppercase" color="tertiary" mt={6}>
                {container.siteName}
              </Text>
            </>
          ) : (
            <>
              <Text size="sm">
                {container.wasteTypeName} · {container.siteName}
              </Text>
              <Text display="technical" size="xs" tt="uppercase" color="tertiary" mt={6}>
                {t("containers:detail.reported", {
                  date: dayjs(container.reported).format("DD MMM"),
                  abs: dayjs(container.reported).format("HH:mm"),
                })}
              </Text>
            </>
          )}
        </div>
      </div>

      <div className={shared.detailBody}>
        {multiMode && (
          <PickupList
            containers={containers}
            onRemove={onRemove}
            removable={!scheduled}
            disabled={scheduleCollection.isPending}
          />
        )}

        <SectionTitle>{t("containers:detail.scheduleCollection")}</SectionTitle>

        <div className={shared.editPanel} style={{ marginTop: 14 }}>
          <DatePickerInput
            label={t("containers:detail.pickDate")}
            placeholder={t("actions:selectDate")}
            value={chosenDate}
            onChange={handleDateChange}
            minDate={new Date()}
            disabled={scheduled || scheduleCollection.isPending}
          />

          {suggestion?.deadlineDate && (
            <Text display="technical" size="xs" tt="uppercase" color="tertiary" mt={8}>
              {t("containers:detail.deadline", { date: dayjs(suggestion.deadlineDate).format("D MMM") })}
            </Text>
          )}

          {/* The site's arrival windows, or a free time entry when it publishes none. */}
          {!scheduled && awaitingSuggestion && <SectionLoader />}

          {!scheduled && !awaitingSuggestion && slotMode && (
            <CollectionSlotSection
              slots={slots}
              value={selectedSlotId}
              onChange={setSelectedSlotId}
              loading={slotsLoading}
              disabled={scheduleCollection.isPending}
            />
          )}

          {!scheduled && !awaitingSuggestion && !slotMode && (
            <div style={{ marginTop: 16 }}>
              <SectionTitle>{t("containers:detail.pickTime")}</SectionTitle>
              <div style={{ marginTop: 10 }}>
                <TimePicker
                  format="24h"
                  value={chosenTime}
                  onChange={setChosenTime}
                  disabled={scheduleCollection.isPending}
                />
              </div>
            </div>
          )}
        </div>

        <div className={shared.divider}>
          <div className={shared.detailSectionHeader}>
            <SectionTitle>{t("containers:detail.assignDriver")}</SectionTitle>
            {!scheduled && !driverAreaBusy && timeChosen && (
              <Text span display="technical" size="xs" tt="uppercase" color="secondary">
                {t("containers:detail.fleetMeta", { available: drivers.length, total: fleetSize })}
              </Text>
            )}
          </div>

          {/* Looking the drivers up, or booking the pickup with the one just picked. */}
          {!scheduled && driverAreaBusy && <SectionLoader />}

          {!scheduled && !driverAreaBusy && !timeChosen && (
            <div className={shared.sectionNote}>
              <Text size="sm" color="tertiary">
                {slotMode ? t("containers:detail.slotDriverLocked") : t("containers:detail.driverLocked")}
              </Text>
            </div>
          )}

          {!scheduled && !driverAreaBusy && timeChosen && (
            <div className={shared.driverList} style={{ marginTop: 12 }}>
              {drivers.map((d) => (
                <button
                  key={d.userId}
                  type="button"
                  onClick={() => scheduleCollection.mutate(d.userId)}
                  className={shared.driverRow}
                >
                  <Avatar initials={initialsOf(d.name)} size={34} />
                  <span className={shared.driverMeta}>
                    <span className={shared.driverName}>
                      <Text span size="sm" strong>
                        {d.name}
                      </Text>
                    </span>
                  </span>
                  <Text span display="technical" size="xs" tt="uppercase" color="success">
                    {t("containers:driver.available")}
                  </Text>
                </button>
              ))}
              {drivers.length === 0 && (
                <div className={shared.sectionNote}>
                  <Text size="sm" color="tertiary">
                    {t("containers:detail.noDrivers")}
                  </Text>
                </div>
              )}
            </div>
          )}

          {scheduled && (
            <div className={styles.assignedCard} style={{ marginTop: 12 }}>
              <div className={styles.assignedTop}>
                <Avatar initials={initialsOf(scheduledDriver ?? "")} size={38} />
                <div style={{ flex: 1 }}>
                  <Text size="md" strong>
                    {scheduledDriver}
                  </Text>
                </div>
              </div>
              <div className={styles.assignedFooter}>
                <SVGAsset name="checkmark-green" width={15} height={15} />
                <Text size="sm" color="success">
                  {multiMode
                    ? t("containers:multiselect.scheduled", { count: containers.length })
                    : t("containers:detail.collectionScheduled")}
                </Text>
              </div>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}

/** Multiselect with nothing ticked yet: says what the mode is for, and offers no schedule controls. */
export function MultiselectEmptyPanel() {
  const { t } = useTranslation();
  return (
    <aside className={shared.detailPanel}>
      <div className={shared.detailHeader}>
        <div className={shared.detailHeaderText}>
          <Text size="sm">{t("containers:multiselect.panelTitleEmpty")}</Text>
          <Text display="technical" size="xs" tt="uppercase" color="tertiary" mt={6}>
            {t("containers:multiselect.panelSubEmpty")}
          </Text>
        </div>
      </div>
      <div className={shared.detailBody}>
        <div className={styles.emptyNote}>
          <SVGAsset name="info" width={16} height={16} className={styles.infoIcon} />
          <Text size="sm" color="tertiary">
            {t("containers:multiselect.emptyNote")}
          </Text>
        </div>
      </div>
    </aside>
  );
}

/** "Containers in this pickup": one row per ticked container, each removable until the pickup is booked. */
const PickupList: FC<{
  containers: FullContainerOverview[];
  onRemove?: (containerId: number) => void;
  removable: boolean;
  disabled: boolean;
}> = ({ containers, onRemove, removable, disabled }) => {
  const { t } = useTranslation();
  return (
    <div className={styles.pickupSection}>
      <SectionTitle>{t("containers:multiselect.inThisPickup")}</SectionTitle>
      <ul className={styles.pickupList}>
        {containers.map((c) => (
          <li key={c.containerId} className={styles.pickupRow}>
            <Text span size="sm" className={styles.pickupLabel}>
              {c.wasteTypeName} · {c.siteName}
            </Text>
            <Text span display="technical" size="xs" tt="uppercase" color="tertiary" nowrap>
              {dayjs(c.reported).format("DD MMM · HH:mm")}
            </Text>
            {removable && (
              <button
                type="button"
                className={styles.removeBtn}
                aria-label={t("containers:multiselect.remove")}
                disabled={disabled}
                onClick={() => onRemove?.(c.containerId)}
              >
                <SVGAsset name="close" width={12} height={12} />
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};
